import { Commodity, NormalizedLoad } from 'types/Load';
import { ManualFieldError } from 'utils/formValidation';

import { isValidMGBStopState } from './mgbGeography';
import {
  ensureMGBRouteStops,
  ensureValidMGBCommodityLegIndices,
  isMGBDropStopType,
  isMGBPickupStopType,
  mgbStopLabel,
} from './mgbStops';
import {
  commodityEffectiveWeight,
  isEmptyMGBCommodity,
  isMGBRatePerMileMode,
  isPositiveMGBDimension,
  isSystemManagedRef,
} from './utils';

function commodityHasDimensions(c: Commodity): boolean {
  return (
    isPositiveMGBDimension(c.length) ||
    isPositiveMGBDimension(c.width) ||
    isPositiveMGBDimension(c.height)
  );
}

/**
 * Client-side guard before submit; mirrors BE validateMGBCommodities.
 *
 * Returns field-level errors keyed by RHF path so the caller can route them
 * through `applyManualValidationErrors` (field errors + accordion expansion +
 * scroll-to-error), matching other load-building forms. Piece/weight units are
 * always required; dimension UOM is required when any dimension is non-zero.
 */
export function validateMGBCommoditiesForSubmit(
  commodities: Commodity[] | undefined
): ManualFieldError<NormalizedLoad>[] {
  const list = commodities ?? [];
  const hasNonEmpty = list.some((c) => !isEmptyMGBCommodity(c));
  if (!hasNonEmpty) {
    // Field-level errors so every required commodity input is flagged,
    // not a single summary under Description.
    return [
      {
        path: 'commodities.0.description',
        message: 'Description is required.',
      },
      {
        path: 'commodities.0.totalPieces.val',
        message: 'Pieces is required.',
      },
      {
        path: 'commodities.0.weightTotal',
        message: 'Weight is required.',
      },
    ];
  }

  const errors: ManualFieldError<NormalizedLoad>[] = [];

  list.forEach((c, index) => {
    if (isEmptyMGBCommodity(c)) {
      return;
    }

    const pieces = Number(c.totalPieces?.val);
    if (!Number.isFinite(pieces) || pieces <= 0) {
      errors.push({
        path: `commodities.${index}.totalPieces.val`,
        message: 'Pieces is required.',
      });
    }
    if (!String(c.totalPieces?.unit ?? '').trim()) {
      errors.push({
        path: `commodities.${index}.totalPieces.unit`,
        message: 'Required',
      });
    }

    const weight = commodityEffectiveWeight(c);
    if (!(weight > 0)) {
      errors.push({
        path: `commodities.${index}.weightTotal`,
        message: 'Weight is required.',
      });
    }
    if (!String(c.weightUnit ?? c.grossWeight?.unit ?? '').trim()) {
      errors.push({
        path: `commodities.${index}.weightUnit`,
        message: 'Weight unit of measure is required.',
      });
    }

    if (commodityHasDimensions(c) && !String(c.dimensionUnit ?? '').trim()) {
      errors.push({
        path: `commodities.${index}.dimensionUnit`,
        message:
          'Dimension unit of measure is required when length, width, or height is set.',
      });
    }

    if (!c.description?.trim()) {
      errors.push({
        path: `commodities.${index}.description`,
        message: 'Description is required.',
      });
    }
  });

  return errors;
}

/**
 * Requires a MG code-table type and a value on every user-editable reference
 * row. Those rows are rendered as Type/Value boxes, including a freshly added
 * empty box. System-managed rows stay optional: they are not those boxes.
 */
export function validateMGBAdditionalReferencesForSubmit(
  refs: NormalizedLoad['additionalReferences'] | undefined
): ManualFieldError<NormalizedLoad>[] {
  const errors: ManualFieldError<NormalizedLoad>[] = [];

  (refs ?? []).forEach((ref, index) => {
    const qualifier = String(ref?.qualifier ?? '').trim();
    const number = String(ref?.number ?? '').trim();
    // System-managed rows (Quote Number, Rate Min/Max, special-reference
    // dropdowns) are hidden from the Type/Value list, so an error on them
    // would block submit on a field the user cannot see or edit.
    if (isSystemManagedRef(qualifier)) {
      return;
    }
    if (!qualifier) {
      errors.push({
        path: `additionalReferences.${index}.qualifier`,
        message: 'Missing reference type.',
      });
    }
    if (!number) {
      errors.push({
        path: `additionalReferences.${index}.number`,
        message: 'Reference value is required.',
      });
    }
  });

  return errors;
}

export function validateMGBRatesForSubmit(
  rateData: NormalizedLoad['rateData'] | undefined
): ManualFieldError<NormalizedLoad>[] {
  const errors: ManualFieldError<NormalizedLoad>[] = [];
  const miles = Number(rateData?.miles);
  const milesValid = Number.isFinite(miles) && miles > 0;

  // Rate Per Mile: buildRateData wires PM with 0 line haul / numUnits when incomplete.
  if (isMGBRatePerMileMode(rateData?.customerLineHaulUnit)) {
    if (!milesValid) {
      errors.push({
        path: 'rateData.miles',
        message:
          'Total miles are required for Rate Per Mile. Set complete pickup and delivery locations.',
      });
    }
    const ratePerMile = Number(rateData?.customerLineHaulRate);
    if (!Number.isFinite(ratePerMile) || ratePerMile <= 0) {
      errors.push({
        path: 'rateData.customerLineHaulRate',
        message: 'Rate per mile is required.',
      });
    }
  }

  const fscPerMile = Number(rateData?.fscPerMile);
  if (Number.isFinite(fscPerMile) && fscPerMile > 0 && !milesValid) {
    const hasMilesError = errors.some((e) => e.path === 'rateData.miles');
    if (!hasMilesError) {
      errors.push({
        path: 'rateData.miles',
        message: 'Distance (miles) is required for PerMile FSC.',
      });
    }
  }

  // buildRateData only wires items with a label; block charged accessorials
  // that would otherwise be dropped on create/update.
  (rateData?.customerLineItems ?? []).forEach((item, index) => {
    const amount = Number(item?.totalChargeUSD);
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    if (String(item?.label ?? '').trim()) {
      return;
    }
    errors.push({
      path: `rateData.customerLineItems.${index}.label`,
      message: 'Accessorial service is required when an amount is set.',
    });
  });

  return errors;
}

// Shape check for US state and Canadian province codes (both are 2 letters).
const TWO_LETTER_CODE_PATTERN = /^[A-Za-z]{2}$/;

/**
 * Anita SIT: stop state must be a valid 2-letter US state or Canadian province code.
 * Country is not validated here — the UI field was removed and submit overwrites
 * country from state via resolveMGBStopCountryForSubmit.
 */
export function validateMGBStopStatesForSubmit(
  values: Pick<NormalizedLoad, 'pickup' | 'consignee' | 'stops'>
): ManualFieldError<NormalizedLoad>[] {
  const errors: ManualFieldError<NormalizedLoad>[] = [];
  const stops = values.stops ?? [];

  if (stops.length >= 2) {
    stops.forEach((stop, index) => {
      const state = String(stop?.address?.state ?? '').trim();
      if (!state) {
        return;
      }
      if (!TWO_LETTER_CODE_PATTERN.test(state)) {
        errors.push({
          path: `stops.${index}.address.state`,
          message: 'State must be a 2-letter code (e.g. FL).',
        });
      } else if (!isValidMGBStopState(state)) {
        errors.push({
          path: `stops.${index}.address.state`,
          message:
            'Enter a valid US state or Canadian province code (e.g. FL, ON).',
        });
      }
    });
    return errors;
  }

  for (const stopKey of ['pickup', 'consignee'] as const) {
    const stop = values[stopKey];
    const state = String(stop?.state ?? '').trim();
    if (state) {
      if (!TWO_LETTER_CODE_PATTERN.test(state)) {
        errors.push({
          path: `${stopKey}.state`,
          message: 'State must be a 2-letter code (e.g. FL).',
        });
      } else if (!isValidMGBStopState(state)) {
        errors.push({
          path: `${stopKey}.state`,
          message:
            'Enter a valid US state or Canadian province code (e.g. FL, ON).',
        });
      }
    }
  }

  return errors;
}

/**
 * Multi-stop topology (ENG-7694 / Kenco):
 * - First stop must be pickup, last must be drop (middle may interleave).
 * - Every non-empty commodity is assigned pickup → drop (missing values
 *   resolve to first pickup / last drop for ease of use).
 * - Every pickup and drop stop must have at least one commodity assigned
 *   (MercuryGate ImportWeb requires shipments on each pickup event).
 */
export function validateMGBMultiStopsForSubmit(
  values: Pick<NormalizedLoad, 'stops' | 'commodities'>
): ManualFieldError<NormalizedLoad>[] {
  const errors: ManualFieldError<NormalizedLoad>[] = [];
  const stops = ensureMGBRouteStops(values as NormalizedLoad).map((s, i) => ({
    ...s,
    stopNumber: i,
  }));
  if (stops.length <= 2) {
    return errors;
  }

  if (!isMGBPickupStopType(stops[0]?.stopType)) {
    errors.push({
      path: 'stops.0.stopType',
      message: 'The first stop must be a pickup.',
    });
  }
  const lastIdx = stops.length - 1;
  if (!isMGBDropStopType(stops[lastIdx]?.stopType)) {
    errors.push({
      path: `stops.${lastIdx}.stopType`,
      message: 'The last stop must be a drop.',
    });
  }

  const pickupIndexes = stops
    .map((s, i) => (isMGBPickupStopType(s.stopType) ? i : -1))
    .filter((i) => i >= 0);
  const dropIndexes = stops
    .map((s, i) => (isMGBDropStopType(s.stopType) ? i : -1))
    .filter((i) => i >= 0);

  if (pickupIndexes.length < 1) {
    errors.push({
      path: 'stops.0.stopType',
      message: 'At least one pickup stop is required.',
    });
  }
  if (dropIndexes.length < 1) {
    errors.push({
      path: `stops.${Math.max(lastIdx, 0)}.stopType`,
      message: 'At least one drop stop is required.',
    });
  }

  const commodities = values.commodities ?? [];

  const resolvedAssignments: Array<{ pickup: number; drop: number }> = [];

  commodities.forEach((c, index) => {
    if (isEmptyMGBCommodity(c)) {
      return;
    }

    // Same resolver as wire prep / stop remap — stale or out-of-range indices
    // must still count toward drop coverage (UI can show Drop 2 while raw
    // values briefly point at a pickup after route edits).
    const leg = ensureValidMGBCommodityLegIndices(
      stops,
      c.pickupStopNumber,
      c.dropStopNumber
    );

    const isValidLeg =
      isMGBPickupStopType(stops[leg.pickup]?.stopType) &&
      isMGBDropStopType(stops[leg.drop]?.stopType) &&
      leg.pickup < leg.drop;

    if (!isValidLeg) {
      errors.push({
        path: `commodities.${index}.dropStopNumber`,
        message: 'Assign a pickup and drop stop for this item.',
      });
    } else {
      resolvedAssignments.push({ pickup: leg.pickup, drop: leg.drop });
    }
  });

  const stopWithoutFreightMessage = (stopIdx: number) =>
    `${mgbStopLabel(stops, stopIdx)} is not assigned to any commodity. Please assign it in Specs first, then try again.`;

  pickupIndexes.forEach((pickupIdx) => {
    const hasFreight = resolvedAssignments.some((a) => a.pickup === pickupIdx);
    if (!hasFreight) {
      errors.push({
        path: 'commodities' as any,
        message: stopWithoutFreightMessage(pickupIdx),
      });
    }
  });

  dropIndexes.forEach((dropIdx) => {
    const hasFreight = resolvedAssignments.some((a) => a.drop === dropIdx);
    if (!hasFreight) {
      errors.push({
        path: 'commodities' as any,
        message: stopWithoutFreightMessage(dropIdx),
      });
    }
  });

  return errors;
}
